/**
 * Zod-validated environment access.
 *
 * Vite injects `import.meta.env.*` at build time. We parse it once on
 * module load so a missing or malformed value fails fast at startup
 * instead of silently `undefined`-ing somewhere deep in an Amplify
 * call. See `frontend/.env.example` for the required vars.
 */
import { z } from 'zod';

const EnvSchema = z.object({
  VITE_COGNITO_REGION: z.string().min(1, 'VITE_COGNITO_REGION is required'),
  VITE_COGNITO_USER_POOL_ID: z.string().min(1, 'VITE_COGNITO_USER_POOL_ID is required'),
  VITE_COGNITO_USER_POOL_CLIENT_ID: z
    .string()
    .min(1, 'VITE_COGNITO_USER_POOL_CLIENT_ID is required'),
  VITE_COGNITO_DOMAIN: z
    .string()
    .min(
      1,
      'VITE_COGNITO_DOMAIN is required (e.g. avatar-factory.auth.eu-central-1.amazoncognito.com)',
    ),
  VITE_OAUTH_REDIRECT_SIGN_IN: z.string().url('VITE_OAUTH_REDIRECT_SIGN_IN must be a full URL'),
  VITE_OAUTH_REDIRECT_SIGN_OUT: z.string().url('VITE_OAUTH_REDIRECT_SIGN_OUT must be a full URL'),
  VITE_API_BASE_URL: z.string().url('VITE_API_BASE_URL must be a full URL'),
  VITE_APP_ENV: z.enum(['local', 'staging', 'production']).default('local'),
  // PostHog product analytics. The project API key is publishable (it ships
  // in the bundle); empty disables analytics entirely. The host defaults to
  // PostHog US cloud. Both are injected at build time by CodeBuild (see
  // infra_cdk/components/cicd.py::FrontendBuildProject).
  VITE_POSTHOG_KEY: z.string().optional().default(''),
  VITE_POSTHOG_HOST: z
    .string()
    .url('VITE_POSTHOG_HOST must be a full URL')
    .optional()
    .default('https://us.i.posthog.com'),
  // Dev-only. Set by the design-parity tooling so `apiFetch` issues
  // same-origin `/api/...` requests that Vite proxies to the backend
  // (see vite.config.ts). Unset in normal dev and in production.
  VITE_DEV_API_PROXY: z.string().optional(),
});

export type AppEnv = z.infer<typeof EnvSchema>;

function parseEnv(): AppEnv {
  const parsed = EnvSchema.safeParse(import.meta.env);
  if (parsed.success) {
    return parsed.data;
  }
  const issues = parsed.error.issues.map((i) => `  - ${i.path.join('.')}: ${i.message}`).join('\n');
  throw new Error(
    `Invalid environment configuration. Check frontend/.env.local against frontend/.env.example.\n${issues}`,
  );
}

export const env: AppEnv = parseEnv();

export const isProduction = env.VITE_APP_ENV === 'production';
export const isLocal = env.VITE_APP_ENV === 'local';
